import { router } from '@inertiajs/react'
import {
  CrownIcon,
  MailIcon,
  UserRoundIcon,
  Building2Icon,
  MapPinIcon,
  BriefcaseMedicalIcon,
  ShieldCheckIcon,
  BadgeCheckIcon,
} from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Separator } from '@/components/ui/separator'

interface User {
  id: number
  name: string
  email: string
  admin: boolean
  created_at: string
  invitation_pending: boolean
  invitation_accepted: boolean
  active: boolean
  // Clinician profile fields
  credentials: string[]
  credentials_display?: string
  specialty?: string
  npi_number?: string
  state_license_numbers: string[]
  license_states: string[]
  board_certifications: string[]
  // Practice & Affiliation
  practice_name?: string
  practice_type?: string
  practice_type_label?: string
  affiliated_organizations: string[]
  practice_addresses: string[]
  zip_codes_served: string[]
  correctional_facility_access: boolean
  community_health_center_affiliation: boolean
  // Care Cascade Roles
  care_cascade_roles: string[]
  care_cascade_roles_display?: string
}

interface AdminUserShowProps {
  auth: {
    user: User
  }
  user: User
}

export default function AdminUserShow({ user }: AdminUserShowProps) {
  const getRoleInfo = () => {
    if (user.admin) {
      return {
        label: 'Admin',
        icon: <CrownIcon className="size-4" />,
        variant: 'default' as const,
        color: 'text-amber-600 dark:text-amber-400',
      }
    }
    return {
      label: 'User',
      icon: <UserRoundIcon className="size-4" />,
      variant: 'outline' as const,
      color: 'text-blue-600 dark:text-blue-400',
    }
  }

  const getUserInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/)
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }

  const roleInfo = getRoleInfo()

  // Check if user has any clinician profile info
  const hasCredentials = user.credentials?.length > 0 ||
    user.specialty ||
    user.npi_number ||
    user.license_states?.length > 0 ||
    user.board_certifications?.length > 0

  const hasPracticeInfo = user.practice_name ||
    user.practice_type ||
    user.affiliated_organizations?.length > 0 ||
    user.practice_addresses?.length > 0 ||
    user.zip_codes_served?.length > 0 ||
    user.correctional_facility_access ||
    user.community_health_center_affiliation

  const hasCareRoles = user.care_cascade_roles?.length > 0

  const hasClinicianProfile = hasCredentials || hasPracticeInfo || hasCareRoles

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={() => router.visit('/admin/users')}>
          Back to Users
        </Button>
        <Button size="sm" onClick={() => router.visit(`/admin/users/${user.id}/edit`)}>
          Edit User
        </Button>
      </div>

      {/* Basic Info Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <Avatar className="size-20">
              <AvatarImage src="" alt={user.name} />
              <AvatarFallback className="text-xl">{getUserInitials(user.name)}</AvatarFallback>
            </Avatar>
            <div>
              <CardTitle className="text-2xl">
                {user.credentials_display ? `${user.name}, ${user.credentials_display}` : user.name}
              </CardTitle>
              <CardDescription>{user.email}</CardDescription>
              {user.specialty && (
                <p className="text-sm text-muted-foreground mt-1">{user.specialty}</p>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={roleInfo.variant} className="flex items-center gap-1">
              {roleInfo.icon}
              {roleInfo.label}
            </Badge>
            {user.active ? (
              <Badge variant="default" className="bg-green-600 hover:bg-green-700">Active</Badge>
            ) : user.invitation_pending ? (
              <Badge variant="secondary">Pending</Badge>
            ) : (
              <Badge variant="outline">Inactive</Badge>
            )}
            {user.credentials?.map((credential) => (
              <Badge key={credential} variant="secondary">{credential.toUpperCase()}</Badge>
            ))}
          </div>

          <div>
            <div className="text-sm font-medium text-muted-foreground">Member Since</div>
            <div className="text-sm">{new Date(user.created_at).toLocaleDateString()}</div>
          </div>

          {user.invitation_pending && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950 p-4">
              <div className="flex items-start gap-3">
                <MailIcon className="size-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                <div className="flex-1 space-y-1">
                  <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                    Invitation Pending
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    This user hasn't accepted their invitation yet.
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => router.post(`/admin/users/${user.id}/resend_invitation`)}
                >
                  Resend Invitation
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Clinician Profile Section */}
      {hasClinicianProfile && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BriefcaseMedicalIcon className="size-5" />
              Clinician Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Credentials & Identity */}
            {hasCredentials && (
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                  <BadgeCheckIcon className="size-4" />
                  Credentials & Identity
                </h4>
                <div className="grid gap-4 md:grid-cols-2">
                  {user.npi_number && (
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">NPI Number</div>
                      <div className="text-sm font-mono">{user.npi_number}</div>
                    </div>
                  )}
                  {user.specialty && (
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">Specialty</div>
                      <div className="text-sm">{user.specialty}</div>
                    </div>
                  )}
                  {user.license_states?.length > 0 && (
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">License States</div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {user.license_states.map((state) => (
                          <Badge key={state} variant="outline" className="text-xs">{state}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {user.state_license_numbers?.length > 0 && (
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">License Numbers</div>
                      <div className="text-sm font-mono">{user.state_license_numbers.join(', ')}</div>
                    </div>
                  )}
                </div>
                {user.board_certifications?.length > 0 && (
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Board Certifications</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {user.board_certifications.map((cert) => (
                        <Badge key={cert} variant="secondary" className="text-xs">{cert}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {hasCredentials && hasPracticeInfo && <Separator />}

            {/* Practice & Affiliation */}
            {hasPracticeInfo && (
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                  <Building2Icon className="size-4" />
                  Practice & Affiliation
                </h4>
                <div className="grid gap-4 md:grid-cols-2">
                  {user.practice_name && (
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">Practice Name</div>
                      <div className="text-sm">{user.practice_name}</div>
                    </div>
                  )}
                  {user.practice_type_label && (
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">Practice Type</div>
                      <div className="text-sm">{user.practice_type_label}</div>
                    </div>
                  )}
                  {user.affiliated_organizations?.length > 0 && (
                    <div className="md:col-span-2">
                      <div className="text-sm font-medium text-muted-foreground">Affiliated Organizations</div>
                      <div className="text-sm">{user.affiliated_organizations.join(', ')}</div>
                    </div>
                  )}
                </div>

                {user.practice_addresses?.length > 0 && (
                  <div>
                    <div className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                      <MapPinIcon className="size-3" />
                      Practice Addresses
                    </div>
                    <div className="mt-1 space-y-1">
                      {user.practice_addresses.map((address, idx) => (
                        <div key={idx} className="text-sm">{address}</div>
                      ))}
                    </div>
                  </div>
                )}

                {user.zip_codes_served?.length > 0 && (
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">ZIP Codes Served</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {user.zip_codes_served.map((zip) => (
                        <Badge key={zip} variant="outline" className="text-xs font-mono">{zip}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-4">
                  {user.correctional_facility_access && (
                    <div className="flex items-center gap-2 text-sm">
                      <ShieldCheckIcon className="size-4 text-green-600" />
                      <span>Correctional Facility Access</span>
                    </div>
                  )}
                  {user.community_health_center_affiliation && (
                    <div className="flex items-center gap-2 text-sm">
                      <ShieldCheckIcon className="size-4 text-green-600" />
                      <span>CHC Affiliation</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {(hasCredentials || hasPracticeInfo) && hasCareRoles && <Separator />}

            {/* Care Cascade Roles */}
            {hasCareRoles && (
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Care Cascade Roles
                </h4>
                <div className="flex flex-wrap gap-2">
                  {user.care_cascade_roles_display ? (
                    user.care_cascade_roles_display.split(', ').map((role) => (
                      <Badge key={role} variant="default">{role}</Badge>
                    ))
                  ) : (
                    user.care_cascade_roles.map((role) => (
                      <Badge key={role} variant="default">{role.replace(/_/g, ' ')}</Badge>
                    ))
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
